<template>
    <VJoyDialog
        v-if="!isAccepted"
        open
        pattern-confirm
        class="legal-agreement-acceptance"
        data-testid="legal-agreement-acceptance-dialog"
        @vue:mounted="trackPopupViewed"
    >
        <template #dialog-preheader>
            <VJoyWrapper justify="center" align="center" class="joy-mb-6">
                <MaltIllustration name="contract-on-malt--high" alt="" data-testid="legal-agreement-acceptance-illustration" width="200" />
            </VJoyWrapper>
        </template>
        <template #dialog-header>{{ t('compliance.legal.agreement.acceptance.umbrella.title') }}</template>
        <template #dialog-body>
            <VJoyHighlight v-if="hasVersionConflict" level="warning" role="alert" class="joy-mb-4" data-testid="legal-agreement-acceptance-updated">
                {{ t('compliance.legal.agreement.acceptance.umbrella.updated') }}
            </VJoyHighlight>
            <p class="joy-m-0">
                {{ t('compliance.legal.agreement.acceptance.umbrella.description') }}
            </p>
            <VJoyText class="joy-mb-0 joy-mt-4" data-testid="legal-agreement-acceptance-content">
                {{ t('compliance.legal.agreement.acceptance.umbrella.read.prefix') }}
                <VJoyLink
                    :href="agreementPdfUrl"
                    :text="t('compliance.legal.agreement.acceptance.umbrella.read.link')"
                    target="_blank"
                    rel="noopener noreferrer"
                />
            </VJoyText>
        </template>
        <template #dialog-confirm>
            <VJoyButton
                variant="main"
                data-testid="legal-agreement-acceptance-confirm"
                :loading="isAccepting"
                :disabled="isAccepting"
                @click="acceptanceStore.acceptAgreement(props.companyId, props.versionId)"
            >
                {{ t('compliance.legal.agreement.acceptance.umbrella.cta') }}
            </VJoyButton>
        </template>
    </VJoyDialog>
</template>

<script setup lang="ts">
    import {computed, onBeforeUnmount} from 'vue';
    import {storeToRefs} from 'pinia';
    import {MaltIllustration} from '#components';
    import {useDataTracking, useTranslation} from '#imports';
    import {VJoyButton, VJoyDialog, VJoyHighlight, VJoyLink, VJoyText, VJoyWrapper} from '@maltjoy/core-vue';
    import {useLegalAgreementAcceptanceStore} from './legal-agreement-acceptance.store';

    const props = defineProps<{companyId: string; versionId: string}>();
    const {t} = useTranslation();
    const {track} = useDataTracking();
    const acceptanceStore = useLegalAgreementAcceptanceStore();
    const {acceptedVersionId, hasVersionConflict, isAccepting} = storeToRefs(acceptanceStore);
    const isAccepted = computed(() => acceptedVersionId.value === props.versionId);
    const agreementPdfUrl = computed(
        () => `/umbrella/company/${encodeURIComponent(props.companyId)}/commercial-contract/versions/${encodeURIComponent(props.versionId)}/pdf`,
    );

    function trackPopupViewed() {
        void track('umbrella_company_commercial_agreement_popup_viewed', {
            company_id: props.companyId,
            agreement_version_id: props.versionId,
        });
    }

    onBeforeUnmount(() => acceptanceStore.$cleanup());
</script>
